<!-- src/components/modal/project-grid-modal.vue -->
<script setup>
import {
  ref,
  reactive,
  onMounted,
  computed,
  defineExpose,
  onUnmounted,
} from "vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useProjectStore } from "@/stores/projectStore";
import { useCrmClientStore } from "@/stores/crmClientStore";
import { useUserStore } from "@/stores/userStore";
import { toast } from "@/utils/toast";
import svc from "@/services/projects.service";

const editor = ClassicEditor;

const store = useProjectStore();
const clientStore = useCrmClientStore();
const userStore = useUserStore();

/* ====== NEXT CODE ====== */
const nextCodeLoading = ref(false);
const nextCodePreview = ref("");

/* ====== EDIT STATE ====== */
const isEdit = ref(false);
const editingId = ref(null);

/* ====== DELETE STATE ====== */
const deletingId = ref(null);
const deleting = computed(() => store.deletingId === deletingId.value);

function cleanupBackdrops() {
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("padding-right");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

/** Programmatically open delete modal for given project ID */
function openForDelete(id) {
  deletingId.value = id || null;
}

/** Header project id */
const displayProjectId = computed(() => {
  if (isEdit.value) return form.project_code || "—";
  if (nextCodeLoading.value) return "Loading…";
  return nextCodePreview.value || "Auto";
});

const form = reactive({
  project_code: "",
  name: "",
  client_id: null,
  client_name: "",
  start_date: "",
  end_date: "",
  priority: "Medium",
  value: 0,
  total_hours: "",
  extra_time: "",
  status: "Active",
  description: "",
  leader_id: null,
  leader_name: "",
  member_ids: [],
});

/* ====== SEARCHABLE DROPDOWNS STATE ====== */
const ownerQuery = ref("");
const ownerOptions = ref([]);
const ownerOpen = ref(false);
const ownerActiveIndex = ref(0);

const clientQuery = ref("");
const clientOptions = ref([]);
const clientOpen = ref(false);
const clientActiveIndex = ref(0);

const membersQuery = ref("");
const memberOptions = ref([]); // {id, label}
const membersOpen = ref(false);
const membersActiveIndex = ref(0);

/* ====== HELPERS ====== */
function debounce(fn, ms = 300) {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

function containsText(hay, q) {
  return (hay || "").toLowerCase().includes((q || "").toLowerCase().trim());
}

function toDateInputValue(value) {
  if (!value) return "";
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

async function loadNextCode() {
  try {
    nextCodeLoading.value = true;
    nextCodePreview.value = "";
    const { data } = await svc.nextCode();
    nextCodePreview.value = data?.code || "";
    form.project_code = nextCodePreview.value || "";
  } catch {
    nextCodePreview.value = "";
  } finally {
    nextCodeLoading.value = false;
  }
}

async function fetchUsers(q = "") {
  try {
    const rows =
      (await (userStore.list?.({ q, per_page: 50 }) ||
        userStore.fetchAll?.({ q, per_page: 50 }))) || null;

    const arr =
      rows?.data?.data ||
      rows?.data ||
      userStore.items?.value ||
      userStore.items ||
      [];

    const mapped = (arr || []).map((u) => ({
      id: u.id,
      label:
        u.name ||
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.email ||
        `User #${u.id}`,
    }));

    ownerOptions.value = mapped;
    memberOptions.value = mapped;
  } catch (e) {
    toast(
      e?.response?.data?.message || e.message || "Failed to load users",
      "error"
    );
  }
}

async function fetchClients(q = "") {
  try {
    const rows = await clientStore.list({ q, per_page: 50 });
    const list =
      rows?.data?.data || rows?.data || clientStore.clients?.value || [];
    clientOptions.value = (list || []).map((c) => ({
      id: c.id,
      label: c.name || `Client #${c.id}`,
    }));
  } catch (e) {
    toast(
      e?.response?.data?.message || e.message || "Failed to load clients",
      "error"
    );
  }
}

const debUsers = debounce(fetchUsers, 300);
const debClients = debounce(fetchClients, 300);
const debMembersQuery = debounce((q) => (membersQuery.value = q), 150);

/* ====== FILTERED OPTIONS ====== */
const filteredOwnerOptions = computed(() => {
  const q = ownerQuery.value.trim();
  if (!q) return ownerOptions.value;
  return ownerOptions.value.filter((o) => containsText(o.label, q));
});

const filteredClientOptions = computed(() => {
  const q = clientQuery.value.trim();
  if (!q) return clientOptions.value;
  return clientOptions.value.filter((c) => containsText(c.label, q));
});

const filteredMemberOptions = computed(() => {
  const q = (membersQuery.value || "").toLowerCase().trim();
  if (!q) return memberOptions.value;
  return memberOptions.value.filter((m) =>
    (m.label || "").toLowerCase().includes(q)
  );
});

/* ====== SELECTION ====== */
function chooseLeader(opt) {
  form.leader_id = opt?.id || null;
  form.leader_name = opt?.label || "";
  ownerOpen.value = false;
  ownerQuery.value = "";
  ownerActiveIndex.value = 0;
}

function chooseClient(opt) {
  form.client_id = opt?.id || null;
  form.client_name = opt?.label || "";
  clientOpen.value = false;
  clientQuery.value = "";
  clientActiveIndex.value = 0;
}

function toggleMember(id) {
  const idx = form.member_ids.indexOf(id);
  if (idx === -1) form.member_ids.push(id);
  else form.member_ids.splice(idx, 1);
}
function isMemberSelected(id) {
  return form.member_ids.includes(id);
}
function clearMembersSearch() {
  membersQuery.value = "";
  membersActiveIndex.value = 0;
}

/* ====== KEYBOARD NAV (↑ ↓ Enter Esc) ====== */
function moveActive(listLen, activeRef, dir) {
  if (!listLen) return;
  activeRef.value = (activeRef.value + dir + listLen) % listLen;
}

function onOwnerKeydown(e) {
  const len = filteredOwnerOptions.value.length;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    moveActive(len, ownerActiveIndex, 1);
    scrollActiveIntoView(".dq-owner-menu", ownerActiveIndex.value);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    moveActive(len, ownerActiveIndex, -1);
    scrollActiveIntoView(".dq-owner-menu", ownerActiveIndex.value);
  } else if (e.key === "Enter") {
    e.preventDefault();
    const opt = filteredOwnerOptions.value[ownerActiveIndex.value];
    if (opt) chooseLeader(opt);
  } else if (e.key === "Escape") {
    ownerOpen.value = false;
  }
}

function onClientKeydown(e) {
  const len = filteredClientOptions.value.length;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    moveActive(len, clientActiveIndex, 1);
    scrollActiveIntoView(".dq-client-menu", clientActiveIndex.value);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    moveActive(len, clientActiveIndex, -1);
    scrollActiveIntoView(".dq-client-menu", clientActiveIndex.value);
  } else if (e.key === "Enter") {
    e.preventDefault();
    const opt = filteredClientOptions.value[clientActiveIndex.value];
    if (opt) chooseClient(opt);
  } else if (e.key === "Escape") {
    clientOpen.value = false;
  }
}

function onMembersKeydown(e) {
  const len = filteredMemberOptions.value.length;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    moveActive(len, membersActiveIndex, 1);
    scrollActiveIntoView(".dq-members-menu", membersActiveIndex.value);
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    moveActive(len, membersActiveIndex, -1);
    scrollActiveIntoView(".dq-members-menu", membersActiveIndex.value);
  } else if (e.key === "Enter") {
    e.preventDefault();
    const opt = filteredMemberOptions.value[membersActiveIndex.value];
    if (opt) toggleMember(opt.id);
  } else if (e.key === "Escape") {
    membersOpen.value = false;
  }
}

function scrollActiveIntoView(menuSelector, activeIndex) {
  // Find menu currently visible and scroll its active item
  requestAnimationFrame(() => {
    const menu = document.querySelector(menuSelector);
    if (!menu) return;
    const items = menu.querySelectorAll(".dq-dd-item");
    const el = items?.[activeIndex];
    el?.scrollIntoView({ block: "nearest" });
  });
}

/* ====== FORM RESET / OPEN ====== */
function resetForm() {
  Object.assign(form, {
    project_code: "",
    name: "",
    client_id: null,
    client_name: "",
    start_date: "",
    end_date: "",
    priority: "Medium",
    value: 0,
    total_hours: "",
    extra_time: "",
    status: "Active",
    description: "",
    leader_id: null,
    leader_name: "",
    member_ids: [],
  });

  ownerQuery.value = "";
  clientQuery.value = "";
  membersQuery.value = "";

  ownerOpen.value = false;
  clientOpen.value = false;
  membersOpen.value = false;

  ownerActiveIndex.value = 0;
  clientActiveIndex.value = 0;
  membersActiveIndex.value = 0;

  nextCodePreview.value = "";
  nextCodeLoading.value = false;
}

function openForCreate() {
  isEdit.value = false;
  editingId.value = null;
  resetForm();
  loadNextCode();
}

async function openForEdit(id) {
  try {
    const data = await store.get(id);
    isEdit.value = true;
    editingId.value = id;

    Object.assign(form, {
      project_code: data.project_code,
      name: data.name,
      client_id: data.client_id || null,
      client_name: data.client?.name || data.client_name || "",
      start_date: toDateInputValue(data.start_date),
      end_date: toDateInputValue(data.end_date),
      priority: data.priority || "Medium",
      value: data.value ?? 0,
      total_hours: data.total_hours || "",
      extra_time: data.extra_time || "",
      status: data.status || "Active",
      description: data.description || "",
      leader_id: data.leader_id || null,
      leader_name:
        data.leader_name ||
        (data.leader
          ? [data.leader.first_name, data.leader.last_name]
              .filter(Boolean)
              .join(" ")
          : ""),
      member_ids: Array.isArray(data.members)
        ? data.members.map((m) => m.id)
        : Array.isArray(data.member_ids)
        ? data.member_ids
        : [],
    });

    await Promise.all([fetchUsers(""), fetchClients("")]);
  } catch (e) {
    throw e;
  }
}

/* ====== DELETE ====== */
async function confirmDelete() {
  if (!deletingId.value) return;
  try {
    await store.remove(deletingId.value);
    await store.list({ force: true });
    hideModal("delete_project");
    toast("Project deleted", "success");
    deletingId.value = null;
  } catch (e) {
    toast(e?.response?.data?.message || e.message || "Delete failed", "error");
  }
}

/* ====== BOOTSTRAP MODAL HELPERS ====== */
function hideModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  try {
    const { Modal } = require("bootstrap");
    const inst = Modal.getInstance(el) || new Modal(el);
    inst.hide();
  } catch {
    el.classList.remove("show");
    el.style.display = "none";
    el.setAttribute("aria-hidden", "true");
    el.removeAttribute("aria-modal");
    document.body.classList.remove("modal-open");
    document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
  }
}

function closeModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.classList.remove("show");
  el.style.display = "none";
  el.setAttribute("aria-hidden", "true");
  el.removeAttribute("aria-modal");
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

/* close dropdowns when clicking outside */
function onDocClick(e) {
  const root = document.getElementById("add_project");
  if (!root) return;

  const ownerWrap = root.querySelector(".dq-owner-wrap");
  const clientWrap = root.querySelector(".dq-client-wrap");
  const membersWrap = root.querySelector(".dq-members-wrap");

  if (ownerOpen.value && ownerWrap && !ownerWrap.contains(e.target))
    ownerOpen.value = false;
  if (clientOpen.value && clientWrap && !clientWrap.contains(e.target))
    clientOpen.value = false;
  if (membersOpen.value && membersWrap && !membersWrap.contains(e.target))
    membersOpen.value = false;
}

/* ====== CREATE / UPDATE ====== */
async function submit() {
  if (!form.name) return toast("Project name is required", "error");
  if (!form.status) return toast("Status is required", "error");

  const payload = {
    name: form.name,
    client_id: form.client_id,
    start_date: form.start_date || null,
    end_date: form.end_date || null,
    priority: form.priority,
    value: Number(form.value || 0),
    total_hours: form.total_hours || null,
    extra_time: form.extra_time || null,
    status: form.status,
    description: form.description || "",
    leader_id: form.leader_id,
    member_ids: form.member_ids,
  };

  try {
    if (isEdit.value && editingId.value) {
      await store.update(editingId.value, payload);
      toast("Project updated", "success");
    } else {
      const created = await store.create(payload);
      form.project_code = created.project_code;
      toast("Project created", "success");
    }
    setTimeout(() => closeModal("add_project"), 120);
  } catch (e) {
    toast(e?.response?.data?.message || e.message || "Save failed", "error");
  }
}

/* ====== MOUNT ====== */
onMounted(() => {
  debUsers("");
  debClients("");

  const el = document.getElementById("add_project");
  if (el) el.addEventListener("hidden.bs.modal", cleanupBackdrops);

  document.addEventListener("click", onDocClick, true);
});

onUnmounted(() => {
  const el = document.getElementById("add_project");
  if (el) el.removeEventListener("hidden.bs.modal", cleanupBackdrops);

  document.removeEventListener("click", onDocClick, true);
});

defineExpose({ openForCreate, openForEdit, openForDelete, confirmDelete });
</script>

<template>
  <div
    class="modal fade dq-project-modal"
    id="add_project"
    role="dialog"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
  >
    <!-- Bigger + scrollable -->
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content dq-modal-content">
        <div
          class="modal-header dq-modal-header align-items-start justify-content-between"
        >
          <div class="d-flex flex-column">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <h5 class="modal-title mb-0">
                {{ isEdit ? "Edit Project" : "Add Project" }}
              </h5>
              <span class="dq-chip">
                <span class="dq-chip-label">Project ID</span>
                <span class="dq-chip-value">
                  <span
                    v-if="nextCodeLoading && !isEdit"
                    class="d-inline-flex align-items-center"
                  >
                    <span class="spinner-border spinner-border-sm me-1"></span>
                    Loading…
                  </span>
                  <span v-else>{{ displayProjectId }}</span>
                </span>
              </span>
            </div>
            <div class="dq-subtitle">
              Create a project, assign client, leader and members, then add a
              rich description.
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body dq-modal-body">
            <div class="row g-3">
              <!-- Name -->
              <div class="col-12">
                <label class="form-label dq-label">Project Name</label>
                <input
                  v-model.trim="form.name"
                  type="text"
                  class="form-control dq-input"
                  placeholder="e.g. Website Redesign"
                  required
                />
              </div>

              <!-- Client -->
              <div class="col-12 dq-client-wrap">
                <label class="form-label dq-label">Client</label>

                <div class="dq-dd">
                  <button
                    type="button"
                    class="btn btn-white border w-100 dq-dd-btn d-flex align-items-center justify-content-between"
                    @click="clientOpen = !clientOpen"
                  >
                    <span class="d-flex align-items-center gap-2 min-w-0">
                      <span class="dq-ico dq-ico-emerald"
                        ><i class="ti ti-building"></i
                      ></span>
                      <span class="text-truncate">
                        {{ form.client_name || "Select client" }}
                      </span>
                    </span>
                    <i class="ti ti-chevron-down"></i>
                  </button>

                  <div v-show="clientOpen" class="dq-dd-menu dq-client-menu">
                    <div class="dq-dd-search">
                      <span class="dq-search-ico"
                        ><i class="ti ti-search"></i
                      ></span>
                      <input
                        v-model="clientQuery"
                        @input="debClients(clientQuery)"
                        @keydown="onClientKeydown"
                        type="text"
                        class="form-control dq-input dq-dd-input"
                        placeholder="Search clients…"
                        autocomplete="off"
                      />
                    </div>

                    <div class="dq-dd-list">
                      <button
                        v-for="(c, idx) in filteredClientOptions"
                        :key="c.id"
                        type="button"
                        class="dq-dd-item"
                        :class="{ active: idx === clientActiveIndex }"
                        @mouseenter="clientActiveIndex = idx"
                        @click="chooseClient(c)"
                      >
                        <div class="dq-dd-row">
                          <span class="dq-ico dq-ico-emerald"
                            ><i class="ti ti-building"></i
                          ></span>
                          <div class="min-w-0">
                            <div class="dq-dd-title text-truncate">
                              {{ c.label }}
                            </div>
                          </div>
                        </div>
                      </button>

                      <div
                        v-if="filteredClientOptions.length === 0"
                        class="dq-empty"
                      >
                        No client found
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Dates -->
              <div class="col-md-6">
                <label class="form-label dq-label">Start Date</label>
                <input
                  v-model="form.start_date"
                  type="date"
                  class="form-control dq-input"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">End Date</label>
                <input
                  v-model="form.end_date"
                  type="date"
                  class="form-control dq-input"
                />
              </div>

              <!-- Priority / Value -->
              <div class="col-md-6">
                <label class="form-label dq-label">Priority</label>
                <select v-model="form.priority" class="form-select dq-input">
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">Project Value</label>
                <input
                  v-model.number="form.value"
                  type="number"
                  min="0"
                  class="form-control dq-input"
                />
              </div>

              <!-- Hours / Extra -->
              <div class="col-md-6">
                <label class="form-label dq-label">Total Working Hours</label>
                <input
                  v-model="form.total_hours"
                  type="text"
                  class="form-control dq-input"
                  placeholder="e.g. 120h"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">Extra Time</label>
                <input
                  v-model="form.extra_time"
                  type="text"
                  class="form-control dq-input"
                  placeholder="e.g. 10h"
                />
              </div>

              <!-- Leader -->
              <div class="col-12 dq-owner-wrap">
                <label class="form-label dq-label">Project Leader</label>

                <div class="dq-dd">
                  <button
                    type="button"
                    class="btn btn-white border w-100 dq-dd-btn d-flex align-items-center justify-content-between"
                    @click="ownerOpen = !ownerOpen"
                  >
                    <span class="d-flex align-items-center gap-2 min-w-0">
                      <span class="dq-ico dq-ico-indigo"
                        ><i class="ti ti-user-star"></i
                      ></span>
                      <span class="text-truncate">
                        {{ form.leader_name || "Select leader" }}
                      </span>
                    </span>
                    <i class="ti ti-chevron-down"></i>
                  </button>

                  <div v-show="ownerOpen" class="dq-dd-menu dq-owner-menu">
                    <div class="dq-dd-search">
                      <span class="dq-search-ico"
                        ><i class="ti ti-search"></i
                      ></span>
                      <input
                        v-model="ownerQuery"
                        @input="debUsers(ownerQuery)"
                        @keydown="onOwnerKeydown"
                        type="text"
                        class="form-control dq-input dq-dd-input"
                        placeholder="Search users…"
                        autocomplete="off"
                      />
                    </div>

                    <div class="dq-dd-list">
                      <button
                        v-for="(u, idx) in filteredOwnerOptions"
                        :key="u.id"
                        type="button"
                        class="dq-dd-item"
                        :class="{ active: idx === ownerActiveIndex }"
                        @mouseenter="ownerActiveIndex = idx"
                        @click="chooseLeader(u)"
                      >
                        <div class="dq-dd-row">
                          <span class="dq-ico dq-ico-indigo"
                            ><i class="ti ti-user"></i
                          ></span>
                          <div class="min-w-0">
                            <div class="dq-dd-title text-truncate">
                              {{ u.label }}
                            </div>
                          </div>
                        </div>
                      </button>

                      <div
                        v-if="filteredOwnerOptions.length === 0"
                        class="dq-empty"
                      >
                        No leaders found
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Members -->
              <div class="col-12 dq-members-wrap">
                <label class="form-label dq-label">Members</label>

                <div class="dq-pillbox mb-2">
                  <span v-for="id in form.member_ids" :key="id" class="dq-pill">
                    {{
                      memberOptions.find((o) => o.id === id)?.label ||
                      "User #" + id
                    }}
                    <button
                      type="button"
                      class="dq-pill-x"
                      @click="toggleMember(id)"
                    >
                      <i class="ti ti-x"></i>
                    </button>
                  </span>

                  <span v-if="!form.member_ids.length" class="dq-muted"
                    >No members selected</span
                  >
                </div>

                <div class="dq-dd">
                  <button
                    type="button"
                    class="btn btn-white border w-100 dq-dd-btn d-flex align-items-center justify-content-between"
                    @click="membersOpen = !membersOpen"
                  >
                    <span class="d-flex align-items-center gap-2 min-w-0">
                      <span class="dq-ico dq-ico-slate"
                        ><i class="ti ti-users"></i
                      ></span>
                      <span class="text-truncate">
                        {{
                          form.member_ids.length
                            ? form.member_ids.length + " selected"
                            : "Select members"
                        }}
                      </span>
                    </span>
                    <i class="ti ti-chevron-down"></i>
                  </button>

                  <div v-show="membersOpen" class="dq-dd-menu dq-members-menu">
                    <div class="dq-dd-search dq-dd-search-split">
                      <div class="flex-grow-1 position-relative">
                        <span class="dq-search-ico"
                          ><i class="ti ti-search"></i
                        ></span>
                        <input
                          type="text"
                          class="form-control dq-input dq-dd-input"
                          placeholder="Search members…"
                          :value="membersQuery"
                          @input="debMembersQuery($event.target.value)"
                          @keydown="onMembersKeydown"
                        />
                      </div>

                      <button
                        type="button"
                        class="dq-mini-btn"
                        @click="clearMembersSearch"
                        title="Clear"
                      >
                        <i class="ti ti-x"></i>
                      </button>
                    </div>

                    <div class="dq-dd-list dq-members-list">
                      <button
                        v-for="(m, idx) in filteredMemberOptions"
                        :key="m.id"
                        type="button"
                        class="dq-dd-item dq-dd-item-check"
                        :class="{ active: idx === membersActiveIndex }"
                        @mouseenter="membersActiveIndex = idx"
                        @click="toggleMember(m.id)"
                      >
                        <div class="dq-dd-row">
                          <span
                            class="dq-check"
                            :class="{ on: isMemberSelected(m.id) }"
                          >
                            <i class="ti ti-check"></i>
                          </span>
                          <div class="min-w-0">
                            <div class="dq-dd-title text-truncate">
                              {{ m.label }}
                            </div>
                          </div>
                        </div>
                      </button>

                      <div
                        v-if="filteredMemberOptions.length === 0"
                        class="dq-empty"
                      >
                        No members
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div class="col-12">
                <label class="form-label dq-label">Status</label>
                <vue-select
                  v-model="form.status"
                  :options="['Active', 'Inactive']"
                  placeholder="Select Status"
                  class="dq-vselect"
                  :clearable="false"
                />
              </div>

              <!-- Description -->
              <div class="col-12 ckeditor-wrap">
                <label class="form-label dq-label">Description</label>
                <ckeditor :editor="editor" v-model="form.description" />
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-outline-light border dq-btn"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              <span
                v-if="!isEdit && store.savingCreate"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              <span
                v-if="isEdit && store.savingUpdate"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ isEdit ? "Save Changes" : "Create Project" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete -->
  <div class="modal fade" id="delete_project" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">Are you sure you want to delete this project?</p>
          <div class="d-flex justify-content-center">
            <a
              href="javascript:void(0);"
              class="btn btn-light me-3"
              data-bs-dismiss="modal"
              >Cancel</a
            >
            <button
              class="btn btn-danger"
              :disabled="deleting"
              @click="confirmDelete"
            >
              <span
                v-if="deleting"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ========= World-class modal polish ========= */
.dq-project-modal .dq-modal-content {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
  overflow: hidden;
}
.dq-modal-header {
  padding: 16px 18px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.1), #fff);
}
.dq-subtitle {
  margin-top: 6px;
  font-size: 0.85rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-modal-body {
  padding: 18px;
  overflow: auto;
  max-height: calc(100vh - 12rem);
}
.dq-modal-footer {
  padding: 14px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}

/* labels & inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.dq-muted {
  color: rgba(15, 23, 42, 0.55);
  font-size: 0.85rem;
}

/* buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}

/* chip */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.03);
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-chip-label {
  color: rgba(15, 23, 42, 0.6);
  font-weight: 900;
}
.dq-chip-value {
  color: rgba(15, 23, 42, 0.9);
}

/* icons */
.dq-ico {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.dq-ico-indigo {
  background: rgba(99, 102, 241, 0.14);
  color: rgb(79, 70, 229);
}
.dq-ico-emerald {
  background: rgba(16, 185, 129, 0.14);
  color: rgb(5, 150, 105);
}
.dq-ico-slate {
  background: rgba(15, 23, 42, 0.06);
  color: rgba(15, 23, 42, 0.78);
}

/* ========= Custom dropdown (no bootstrap dropdown dependency) ========= */
.dq-dd {
  position: relative;
}
.dq-dd-btn {
  border-radius: 14px;
  padding: 10px 12px;
  background: #fff;
}
.dq-dd-menu {
  position: absolute;
  z-index: 1056; /* above modal body content */
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  background: #fff;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
  overflow: hidden;
}
.dq-dd-search {
  position: relative;
  padding: 10px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-dd-search-split {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dq-search-ico {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(15, 23, 42, 0.45);
}
.dq-dd-input {
  padding-left: 40px;
}
.dq-dd-list {
  max-height: 280px;
  overflow: auto;
  overscroll-behavior: contain;
}
.dq-dd-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 10px 10px;
  border-radius: 0;
  cursor: pointer;
}
.dq-dd-item:hover {
  background: rgba(15, 23, 42, 0.04);
}
.dq-dd-item.active {
  background: rgba(99, 102, 241, 0.12);
}
.dq-dd-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-dd-title {
  font-weight: 900;
}
.dq-empty {
  padding: 14px 12px;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(15, 23, 42, 0.55);
}

/* members list check */
.dq-members-list .dq-dd-item {
  padding: 10px 12px;
}
.dq-check {
  width: 22px;
  height: 22px;
  border-radius: 8px;
  border: 1px solid rgba(15, 23, 42, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  background: #fff;
}
.dq-check.on {
  background: rgba(99, 102, 241, 0.14);
  border-color: rgba(99, 102, 241, 0.25);
  color: rgb(79, 70, 229);
}

/* pillbox */
.dq-pillbox {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 10px 10px;
  border: 1px dashed rgba(15, 23, 42, 0.12);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.02);
}
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
  font-weight: 800;
  font-size: 0.82rem;
}
.dq-pill-x {
  border: 0;
  background: transparent;
  color: rgba(239, 68, 68, 0.95);
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dq-mini-btn {
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.03);
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* CKEditor area comfortable scroll */
.ckeditor-wrap :deep(.ck-editor__editable) {
  min-height: 160px;
  max-height: 320px;
  overflow: auto;
  border-radius: 14px;
}
.ckeditor-wrap :deep(.ck.ck-editor__main > .ck-editor__editable) {
  border-radius: 14px;
}

:deep(#add_project .modal-dialog) {
  height: calc(100vh - 2rem) !important;
  margin: 1rem auto !important;
}

:deep(#add_project .modal-dialog.modal-dialog-centered) {
  align-items: stretch !important; /* IMPORTANT: don't vertically center the dialog box */
}

/* responsive */
@media (max-width: 576px) {
  .dq-modal-body {
    padding: 14px;
  }
  .dq-modal-header {
    padding: 14px;
  }
  .dq-modal-footer {
    padding: 12px 14px;
  }
}
</style>
